import type { Consent, ConsentRequirement } from "./consent";
import {
  rawStringCodec,
  type ConsentStore,
  type StoredValueCodec,
  type SyncStorageArea,
} from "./consent-store";

export const SHOPPER_ID_STORAGE_KEY = "redo.anonymous_shopper_id";
export const PERSISTED_SESSION_ID_STORAGE_KEY =
  "redo.anonymous_shopify_session_id";
/** Stand-in for `_shopify_s` (gone since ~2026-09-25): rotates after 30 min idle. */
export const ROLLING_SESSION_STORAGE_KEY = "redo.rolling_session";
const SHOPIFY_SESSION_COOKIE = "_shopify_s";
const ROLLING_SESSION_ID_PREFIX = "redo-";
export const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

interface RollingSession {
  id: string;
  lastActivityMs: number;
}

const rollingSessionCodec: StoredValueCodec<RollingSession> = {
  parse(raw) {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return undefined;
    }
    const id: unknown = Reflect.get(parsed, "id");
    const lastActivityMs: unknown = Reflect.get(parsed, "lastActivityMs");
    if (
      typeof id !== "string" ||
      !id.startsWith(ROLLING_SESSION_ID_PREFIX) ||
      typeof lastActivityMs !== "number"
    ) {
      return undefined;
    }
    return { id, lastActivityMs };
  },
  serialize: (value) => JSON.stringify(value),
};

//redontIgnore
export interface PageView {
  shopperId: string;
  sessionId: string;
  /** No `_shopify_s` and no rolling session could be stored. */
  sessionFellBackToShopperId: boolean;
}

export interface ShopperIdentity {
  /**
   * Never mints. `undefined` without consent. For point-in-time reads; code
   * that keeps the id while mounted uses `subscribe` (e.g. React's
   * `useSyncExternalStore(identity.subscribe, identity.getShopperId)`).
   */
  getShopperId(): string | undefined;
  /** Resumes or mints the shopper id. `undefined` without consent. */
  ensureShopperId(): string | undefined;
  /**
   * `_shopify_s` if set, else the session the current page view persisted.
   * Never mints or extends.
   */
  getSessionId(): string | undefined;
  /**
   * Once per page load: resumes or mints the shopper id and extends the
   * rolling session. `undefined` without consent; nothing is stored then.
   */
  beginPageView(nowMs?: number): PageView | undefined;
  /**
   * Runs `run` once, as soon as the identifiers may be stored: immediately
   * with consent mode off, else when the visitor grants consent.
   */
  whenAvailable(run: () => void): void;
  /** Runs when consent for the identifiers is withdrawn and they were deleted. */
  onCleared(listener: () => void): void;
  /** Calls `listener` when the shopper id is minted or cleared. Returns an unsubscribe. */
  subscribe(listener: () => void): () => void;
}

//redontIgnore
export interface ShopperIdentityOptions {
  consent: Consent;
  store: ConsentStore;
  localStorage: SyncStorageArea;
  readCookie(name: string): string | undefined;
  randomUUID(): string;
  /** Purposes the identifiers may be stored for; each use still checks its own. */
  requirement?: ConsentRequirement;
}

export function createShopperIdentity({
  consent,
  store,
  localStorage,
  readCookie,
  randomUUID,
  requirement = "analytics",
}: ShopperIdentityOptions): ShopperIdentity {
  const item = <T>(key: string, codec: StoredValueCodec<T>) =>
    store.item({ key, area: localStorage, consent: requirement, codec });
  const shopperId = item(SHOPPER_ID_STORAGE_KEY, rawStringCodec);
  const persistedSessionId = item(
    PERSISTED_SESSION_ID_STORAGE_KEY,
    rawStringCodec,
  );
  const rollingSession = item(ROLLING_SESSION_STORAGE_KEY, rollingSessionCodec);

  const allowed = () => consent.allows(requirement);

  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) {
      listener();
    }
  };
  // Registered after the items, so storage is already cleared when listeners read.
  consent.onDenied(requirement, notify);

  const touchRollingSession = (nowMs: number): string | undefined => {
    const existing = rollingSession.get();
    const id =
      existing && nowMs - existing.lastActivityMs < SESSION_IDLE_TIMEOUT_MS
        ? existing.id
        : `${ROLLING_SESSION_ID_PREFIX}${randomUUID()}`;
    rollingSession.set({ id, lastActivityMs: nowMs });
    // A write that did not land (storage blocked) cannot be a session.
    return rollingSession.get()?.id === id ? id : undefined;
  };

  const ensureShopperId = (): string | undefined => {
    if (!allowed()) {
      return undefined;
    }
    const existing = shopperId.get();
    if (existing) {
      return existing;
    }
    const minted = randomUUID();
    shopperId.set(minted);
    notify();
    return minted;
  };

  return {
    getShopperId: () => shopperId.get(),
    ensureShopperId,
    getSessionId() {
      if (!allowed()) {
        return undefined;
      }
      const cookie = readCookie(SHOPIFY_SESSION_COOKIE);
      if (cookie) {
        return cookie;
      }
      const persisted = persistedSessionId.get();
      // Older page loads persisted the permanent shopper id here.
      if (!persisted || persisted === shopperId.get()) {
        return undefined;
      }
      return persisted;
    },
    beginPageView(nowMs = Date.now()) {
      const id = ensureShopperId();
      if (!id) {
        return undefined;
      }
      const session =
        readCookie(SHOPIFY_SESSION_COOKIE) ?? touchRollingSession(nowMs);
      const sessionId = session ?? id;
      if (persistedSessionId.get() !== sessionId) {
        persistedSessionId.set(sessionId);
      }
      return {
        shopperId: id,
        sessionId,
        sessionFellBackToShopperId: session === undefined,
      };
    },
    whenAvailable(run) {
      consent.whenAllowed(requirement, run);
    },
    onCleared(listener) {
      consent.onDenied(requirement, listener);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
